import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal as AntModal, Button as AntButton, Table, Input as AntInput, message } from 'antd';

const MAX_ROWS = 50;

export const CLIENT_PASTE_COLUMNS = [
  {
    key: '거래처명',
    label: '거래처명',
    required: true,
    width: 160,
    aliases: ['거래처명', '업체명', '회사명', '상호', 'clientname', 'company', 'name'],
  },
  {
    key: '대표자명',
    label: '대표자명',
    required: true,
    width: 110,
    aliases: ['대표자명', '대표자', '담당자명', '담당자', '대표', 'owner', 'manager'],
  },
  {
    key: '대표이메일',
    label: '대표이메일',
    required: true,
    width: 180,
    aliases: ['대표이메일', '이메일', 'email', '메일', 'mail', '아이디', 'loginid'],
  },
  {
    key: '대표전화',
    label: '대표전화',
    required: true,
    width: 130,
    aliases: ['대표전화', '전화번호', '전화', '휴대폰', '연락처', 'phone', 'tel', 'mobile'],
  },
  {
    key: '주소',
    label: '주소',
    required: false,
    width: 200,
    aliases: ['주소', '사업장주소', '기본주소', 'address'],
  },
  {
    key: '상세주소',
    label: '상세주소',
    required: false,
    width: 140,
    aliases: ['상세주소', '주소상세', 'addressdetail'],
  },
  {
    key: '우편번호',
    label: '우편번호',
    required: false,
    width: 90,
    aliases: ['우편번호', 'zip', 'zipcode', 'postal'],
  },
  {
    key: '사업자등록번호',
    label: '사업자등록번호',
    required: false,
    width: 130,
    aliases: ['사업자등록번호', '사업자번호', '사업자', 'businessno', 'bizno'],
  },
  {
    key: '관광사업자등록번호',
    label: '관광사업자등록번호',
    required: false,
    width: 150,
    aliases: ['관광사업자등록번호', '관광사업자번호', '관광사업자', 'tourismno'],
  },
];

const isValidEmailFormat = (value) =>
  /^[a-z0-9](?:[a-z0-9._+-]*[a-z0-9])?@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/i.test(
    String(value || '').trim(),
  );

function emptyRow() {
  const row = { _id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` };
  CLIENT_PASTE_COLUMNS.forEach((c) => {
    row[c.key] = '';
  });
  return row;
}

function normalizeHeader(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_\-]/g, '')
    .replace(/\(.*?\)/g, '');
}

function findColumnKey(header) {
  const norm = normalizeHeader(header);
  if (!norm) return null;
  for (const col of CLIENT_PASTE_COLUMNS) {
    if (col.aliases.some((a) => normalizeHeader(a) === norm)) return col.key;
  }
  return null;
}

function parseTsvText(text) {
  const lines = String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((line) => line.trim().length > 0);
  return lines.map((line) => line.split('\t').map((c) => String(c ?? '').trim()));
}

function parseHtmlTable(html) {
  try {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    const table = doc.querySelector('table');
    if (!table) return [];
    return Array.from(table.querySelectorAll('tr'))
      .map((tr) =>
        Array.from(tr.querySelectorAll('th,td')).map((cell) => String(cell.textContent || '').trim()),
      )
      .filter((row) => row.some((c) => c));
  } catch {
    return [];
  }
}

function mapMatrixToRows(matrix) {
  if (!Array.isArray(matrix) || matrix.length === 0) return [];

  const first = matrix[0] || [];
  const mappedKeys = first.map((h) => findColumnKey(h));
  const headerHitCount = mappedKeys.filter(Boolean).length;
  const hasHeader = headerHitCount >= 2;

  let dataRows = matrix;
  let keyOrder = CLIENT_PASTE_COLUMNS.map((c) => c.key);

  if (hasHeader) {
    dataRows = matrix.slice(1);
    keyOrder = mappedKeys;
  }

  const rows = [];
  for (const cells of dataRows) {
    if (!Array.isArray(cells) || cells.every((c) => !String(c ?? '').trim())) continue;
    const row = emptyRow();
    if (hasHeader) {
      keyOrder.forEach((key, idx) => {
        if (!key) return;
        row[key] = String(cells[idx] ?? '').trim();
      });
    } else {
      keyOrder.forEach((key, idx) => {
        row[key] = String(cells[idx] ?? '').trim();
      });
    }

    const hasAny = CLIENT_PASTE_COLUMNS.some((c) => String(row[c.key] || '').trim());
    if (hasAny) rows.push(row);
    if (rows.length >= MAX_ROWS) break;
  }
  return rows;
}

function parseClipboardData(clipboardData) {
  const html = clipboardData?.getData?.('text/html') || '';
  const text = clipboardData?.getData?.('text/plain') || '';
  const fromHtml = html ? parseHtmlTable(html) : [];
  if (fromHtml.length > 0) return mapMatrixToRows(fromHtml);
  if (text) return mapMatrixToRows(parseTsvText(text));
  return [];
}

function validateRow(row) {
  const name = String(row?.거래처명 || '').trim();
  const owner = String(row?.대표자명 || '').trim();
  const email = String(row?.대표이메일 || '').trim();
  const phone = String(row?.대표전화 || '').trim();

  if (!name) return '거래처명 필수';
  if (!owner) return '대표자명 필수';
  if (!email) return '대표이메일 필수';
  if (!isValidEmailFormat(email)) return '이메일 형식 오류';
  if (!phone) return '대표전화 필수';
  return '';
}

/**
 * 거래처 엑셀/클립보드 붙여넣기 일괄 등록 모달
 * onConfirm(rows) 호출 — rows는 정규화된 거래처 객체 배열
 */
export default function BulkClientPasteModal({
  open,
  onClose,
  onConfirm,
  confirming = false,
  progressText = '',
  existingEmails = [],
}) {
  const [rows, setRows] = useState(() => [emptyRow()]);
  const pasteZoneRef = useRef(null);

  const existingEmailSet = useMemo(() => {
    const set = new Set();
    (Array.isArray(existingEmails) ? existingEmails : []).forEach((e) => {
      const v = String(e || '').trim().toLowerCase();
      if (v) set.add(v);
    });
    return set;
  }, [existingEmails]);

  useEffect(() => {
    if (open) {
      setRows([emptyRow()]);
      setTimeout(() => {
        try {
          pasteZoneRef.current?.focus?.();
        } catch {
          /* ignore */
        }
      }, 100);
    }
  }, [open]);

  const handleClose = () => {
    if (confirming) return;
    setRows([emptyRow()]);
    onClose?.();
  };

  const applyParsedRows = (parsed) => {
    if (!parsed.length) {
      message.warning('붙여넣을 표 데이터를 찾지 못했습니다. 엑셀에서 표 영역을 복사한 뒤 다시 붙여넣으세요.');
      return;
    }
    setRows(parsed);
    message.success(`${parsed.length}건의 거래처가 반영되었습니다.`);
  };

  const handlePaste = (e) => {
    const cd = e?.clipboardData;
    if (!cd) return;
    const parsed = parseClipboardData(cd);
    if (parsed.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    applyParsedRows(parsed);
  };

  const handlePasteFromClipboard = async () => {
    try {
      if (!navigator?.clipboard?.readText) {
        message.info('이 브라우저에서는 클립보드 읽기가 제한됩니다. 표 영역에서 Ctrl+V로 붙여넣으세요.');
        pasteZoneRef.current?.focus?.();
        return;
      }
      const text = await navigator.clipboard.readText();
      const parsed = mapMatrixToRows(parseTsvText(text));
      applyParsedRows(parsed);
    } catch {
      message.info('클립보드를 읽을 수 없습니다. 표 영역에서 Ctrl+V로 붙여넣으세요.');
      pasteZoneRef.current?.focus?.();
    }
  };

  const updateCell = (rowId, key, value) => {
    setRows((prev) => prev.map((r) => (r._id === rowId ? { ...r, [key]: value } : r)));
  };

  const removeRow = (rowId) => {
    setRows((prev) => {
      const next = prev.filter((r) => r._id !== rowId);
      return next.length ? next : [emptyRow()];
    });
  };

  const addEmptyRow = () => {
    setRows((prev) => (prev.length >= MAX_ROWS ? prev : [...prev, emptyRow()]));
  };

  const rowStatuses = useMemo(() => {
    const emailCounts = new Map();
    rows.forEach((r) => {
      const email = String(r?.대표이메일 || '').trim().toLowerCase();
      if (!email) return;
      emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
    });

    return rows.map((r) => {
      const error = validateRow(r);
      const email = String(r?.대표이메일 || '').trim().toLowerCase();
      let warning = '';
      if (!error && email) {
        if (emailCounts.get(email) > 1) warning = '붙여넣기 내 이메일 중복';
        else if (existingEmailSet.has(email)) warning = '이미 등록된 이메일';
      }
      return { error, warning, ok: !error };
    });
  }, [rows, existingEmailSet]);

  const validCount = useMemo(() => rowStatuses.filter((s) => s.ok).length, [rowStatuses]);
  const errorCount = useMemo(() => rowStatuses.filter((s) => s.error).length, [rowStatuses]);
  const filledCount = useMemo(
    () => rows.filter((r) => CLIENT_PASTE_COLUMNS.some((c) => String(r[c.key] || '').trim())).length,
    [rows],
  );

  const handleOk = () => {
    const payload = [];
    const invalid = [];

    rows.forEach((r, idx) => {
      const hasAny = CLIENT_PASTE_COLUMNS.some((c) => String(r[c.key] || '').trim());
      if (!hasAny) return;
      const error = validateRow(r);
      if (error) {
        invalid.push(`${idx + 1}행: ${error}`);
        return;
      }
      const out = {};
      CLIENT_PASTE_COLUMNS.forEach((c) => {
        out[c.key] = String(r[c.key] ?? '').trim();
      });
      payload.push(out);
    });

    if (payload.length === 0) {
      message.warning(
        invalid.length
          ? `등록 가능한 행이 없습니다.\n${invalid.slice(0, 5).join('\n')}`
          : '등록할 거래처가 없습니다. 엑셀 표를 붙여넣으세요.',
      );
      return;
    }

    if (invalid.length > 0) {
      const proceed = window.confirm(
        `유효하지 않은 ${invalid.length}건은 건너뛰고, ${payload.length}건만 등록합니다.\n\n${invalid.slice(0, 8).join('\n')}${invalid.length > 8 ? '\n...' : ''}\n\n계속할까요?`,
      );
      if (!proceed) return;
    }

    onConfirm?.(payload.slice(0, MAX_ROWS));
  };

  const columns = [
    {
      title: 'No',
      width: 48,
      fixed: 'left',
      render: (_v, _r, idx) => idx + 1,
    },
    {
      title: '상태',
      width: 120,
      fixed: 'left',
      render: (_v, _r, idx) => {
        const st = rowStatuses[idx];
        if (!st) return null;
        if (st.error) return <span className="text-xs text-red-600">{st.error}</span>;
        if (st.warning) return <span className="text-xs text-amber-600">{st.warning}</span>;
        const hasAny = CLIENT_PASTE_COLUMNS.some((c) => String(_r[c.key] || '').trim());
        if (!hasAny) return <span className="text-xs text-gray-400">빈 행</span>;
        return <span className="text-xs text-green-600">등록 가능</span>;
      },
    },
    ...CLIENT_PASTE_COLUMNS.map((col) => ({
      title: (
        <span>
          {col.label}
          {col.required ? <span className="text-red-500 ml-0.5">*</span> : null}
        </span>
      ),
      dataIndex: col.key,
      key: col.key,
      width: col.width,
      render: (value, record) => (
        <AntInput
          size="small"
          value={value}
          disabled={confirming}
          onChange={(e) => updateCell(record._id, col.key, e.target.value)}
          placeholder={col.label}
        />
      ),
    })),
    {
      title: '',
      key: 'actions',
      width: 56,
      fixed: 'right',
      render: (_v, record) => (
        <AntButton type="link" size="small" danger disabled={confirming} onClick={() => removeRow(record._id)}>
          삭제
        </AntButton>
      ),
    },
  ];

  return (
    <AntModal
      open={open}
      title="거래처 표 붙여넣기 일괄 등록"
      width={1200}
      centered
      maskClosable={false}
      destroyOnClose
      onCancel={handleClose}
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-gray-500">
            {confirming && progressText ? (
              <span className="text-blue-600 font-medium">{progressText}</span>
            ) : (
              <>
                등록 가능 <span className="font-semibold text-blue-600">{validCount}</span>건
                {errorCount > 0 ? (
                  <>
                    {' · '}오류 <span className="font-semibold text-red-600">{errorCount}</span>건
                  </>
                ) : null}
                {filledCount > MAX_ROWS ? ` (최대 ${MAX_ROWS}건)` : ''}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <AntButton onClick={handleClose} disabled={confirming}>
              취소
            </AntButton>
            <AntButton onClick={handlePasteFromClipboard} disabled={confirming}>
              클립보드에서 붙여넣기
            </AntButton>
            <AntButton onClick={addEmptyRow} disabled={confirming || rows.length >= MAX_ROWS}>
              행 추가
            </AntButton>
            <AntButton type="primary" loading={confirming} onClick={handleOk} disabled={validCount === 0 && !confirming}>
              {validCount > 0 ? `${validCount}건 등록` : '등록'}
            </AntButton>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-relaxed text-gray-700">
          엑셀·스프레드시트에서 거래처 표(헤더 포함 권장)를 복사한 뒤, 아래 표 영역을 클릭하고 <strong>Ctrl+V</strong>로
          붙여넣으세요. 미리보기에서 수정한 뒤 한 번에 등록할 수 있습니다.
          <div className="mt-1 text-gray-500">
            필수: 거래처명, 대표자명, 대표이메일, 대표전화 · 선택: 주소, 상세주소, 우편번호, 사업자등록번호,
            관광사업자등록번호
          </div>
          <div className="mt-1 text-gray-500">헤더가 없으면 위 컬럼 순서(왼쪽부터)로 인식합니다. 최대 {MAX_ROWS}건.</div>
        </div>

        <div
          ref={pasteZoneRef}
          tabIndex={0}
          onPaste={handlePaste}
          className="outline-none rounded-md border border-dashed border-gray-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 p-1"
        >
          <Table
            size="small"
            pagination={false}
            scroll={{ x: 1400, y: 360 }}
            rowKey="_id"
            columns={columns}
            dataSource={rows}
            locale={{ emptyText: '여기 클릭 후 Ctrl+V로 엑셀 표를 붙여넣으세요' }}
          />
        </div>
      </div>
    </AntModal>
  );
}
